import { ifElse } from 'aa_common/front-end/helpers'
import { ASSET_CHANGE_SITUATION_TYPES, FEATURES_ID } from 'common/constants'
import { FeatureFlagNames } from 'common/open-features'
import { useCheckFlag } from 'common/open-features/hooks'
import useUserPermission from 'lib/hooks/useUserPermission'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { selectMonthlyClosingCurrent } from 'store/accounting/monthlyClosing/selectors'
import { selectEventHistories } from 'store/asset/selectors'

export const useCorrection = () => {
  const monthlyClosingCurrent = useSelector(selectMonthlyClosingCurrent, isEqual)
  const eventHistories = useSelector(selectEventHistories, isEqual)
  const { permissions: fixedAssetPermissions } = useUserPermission(FEATURES_ID.FIXED_ASSETS)
  const { isEnable: isCorrectionBlockedReasonEnabled } = useCheckFlag(FeatureFlagNames.AssetCorrectionBlockedReason)
  const isCorrectable = fixedAssetPermissions?.isCRU || fixedAssetPermissions?.isCRUD

  const checkEventsAllowedForCorrection = (eventCode: number) => {
    return [
      ASSET_CHANGE_SITUATION_TYPES.USAGE_STARTED,
      ASSET_CHANGE_SITUATION_TYPES.ACQUISITION,
      ASSET_CHANGE_SITUATION_TYPES.ACCEPTANCE_OF_TRANSITION,
    ].includes(eventCode)
  }

  const eventsWithCorrectionLinkHidden = [
    ASSET_CHANGE_SITUATION_TYPES.SELL,
    ASSET_CHANGE_SITUATION_TYPES.MOVEMENT,
    ASSET_CHANGE_SITUATION_TYPES.RETIREMENT,
    ASSET_CHANGE_SITUATION_TYPES.OTHER_CHANGE,
    ASSET_CHANGE_SITUATION_TYPES.DIVIDING,
    ASSET_CHANGE_SITUATION_TYPES.IMPAIRMENT,
  ]

  const isCorrectionLinkHidden = eventHistories.some(event =>
    eventsWithCorrectionLinkHidden.includes(event.change_situation.code)
  )

  // 除却/売却/減損/分割/移動 of any event history blocks correction (AAISSUES-20688).
  const legacyEventsBlockingCorrection = [
    ASSET_CHANGE_SITUATION_TYPES.RETIREMENT,
    ASSET_CHANGE_SITUATION_TYPES.SELL,
    ASSET_CHANGE_SITUATION_TYPES.IMPAIRMENT,
    ASSET_CHANGE_SITUATION_TYPES.DIVIDING,
    ASSET_CHANGE_SITUATION_TYPES.MOVEMENT,
  ]

  // With the flag on the correction link is always shown and the correction screen explains why it is blocked, so
  // every event that used to hide the link has to produce a reason instead - その他変更 included (AAISSUES-20763).
  const eventsBlockingCorrection = new Set(
    ifElse(isCorrectionBlockedReasonEnabled, eventsWithCorrectionLinkHidden, legacyEventsBlockingCorrection)
  )

  const isCorrectionBlockedByEventHistory = eventHistories.some(event =>
    eventsBlockingCorrection.has(event.change_situation?.code)
  )

  const checkDateWithMonthlyClosing = (date: string) => {
    const monthlyClosingBeginningDate = monthlyClosingCurrent?.from_term_month?.start_date
    // Default to "not blocked" while monthlyClosingCurrent hasn't loaded yet (or failed to load),
    // instead of comparing against `moment(undefined)` (now), which would treat any past date as blocked.
    return !monthlyClosingBeginningDate || moment(date).isSameOrAfter(moment(monthlyClosingBeginningDate))
  }

  return {
    isCorrectable,
    isCorrectionLinkHidden,
    isCorrectionBlockedByEventHistory,
    checkEventsAllowedForCorrection,
    checkDateWithMonthlyClosing,
  }
}
